import { twMerge } from 'tailwind-merge';
import { getPlaceUrl, getRegionUrl } from '@domain/routes/urls';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { Text } from '../Text/Text';
import { variants } from '../Text/variants';
import { ComponentType, FunctionComponent } from 'react';

interface IProps {
  className?: string;
  countryId?: string;
  countryName?: string;
  linkClassName?: string;
  placeName: string;
  placeSlug?: string;
  regionId?: string | null;
  regionName?: string | null;
  textAs?: string | ComponentType<any> | FunctionComponent<any>;
  textVariant?: keyof typeof variants;
}

/**
 * `PlaceLinks` component renders links to a place (city) and region based on provided IDs and names.
 *
 * @param {Object} props - The properties object.
 * @param {string | null} [props.regionId] - The ID of the region (optional).
 * @param {string | null} [props.regionName] - The name of the region (optional).
 * @param {string} props.countryId - The ID of the country.
 * @param {string} props.countryName - The name of the country.
 * @param {string} [props.className] - Additional class names for the `Text` component (optional).
 * @param {string} [props.linkClassName] - Additional class names for the `InternalLink` component (optional).
 * @param {string | ComponentType<any> | FunctionComponent<any>} [props.textAs='p'] - The HTML tag or component to render the text as (optional).
 * @param {keyof typeof variants} [props.textVariant='xs'] - The variant of the text (optional).
 *
 * @returns {JSX.Element} The rendered `PlaceLinks` component.
 */
export const PlaceLinks = ({
  className,
  countryId,
  countryName,
  linkClassName,
  placeName,
  placeSlug,
  regionId,
  regionName,
  textAs = 'p',
  textVariant = 'xs',
}: IProps) => {
  if (!countryId || !regionId || !placeSlug || !countryName || !regionName) {
    return `${placeName}${regionId ? `, ${regionId}` : ''}`;
  }

  return (
    <Text as={textAs} variant={textVariant} className={twMerge('m-0 text-center', className)}>
      <InternalLink className={linkClassName} to={getPlaceUrl(countryId, regionId, placeSlug)}>
        {placeName}
      </InternalLink>
      <span>{', '}</span>
      <InternalLink
        className={linkClassName}
        to={getRegionUrl(countryId, regionId, countryName, regionName)}
      >
        {regionId}
      </InternalLink>
    </Text>
  );
};
